/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Alert,
  Divider,
  TextField
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { getTeamInviteDetails, joinTeamByInviteCode } from '../services/debateService';
import { useTranslation } from 'react-i18next';
import logger from '../utils/logger';
import { useRecaptchaV3 } from '../components/CaptchaComponent';

const TeamInvitePage: React.FC = () => {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation();
  const { executeRecaptcha } = useRecaptchaV3();
  
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const [debate, setDebate] = useState<any>(null);
  const [team, setTeam] = useState<any>(null);
  const [invitePassword, setInvitePassword] = useState('');
  
  // Track authentication changes
  useEffect(() => {
    // If user becomes unauthenticated but there was an inviteCode,
    // store it for later use after login
    if (!isAuthenticated && inviteCode) {
      localStorage.setItem('pendingTeamInvite', inviteCode);
    }
  }, [isAuthenticated, inviteCode]);
  
  // Debugging for the inviteCode parameter
  useEffect(() => {
    logger.info('Team Invite Code from URL:', inviteCode);
  }, [inviteCode]);
  
  // Fetch team information by invite code
  useEffect(() => {
    const fetchTeamInvite = async () => {
      try {
        setLoading(true);
        const response = await getTeamInviteDetails(inviteCode!);
        
        if (response && response.debate) {
          logger.info('TeamInvitePage: Complete debate data:', response.debate);
          logger.info('TeamInvitePage: Received debate data:', {
            id: response.debate._id,
            topic: response.debate.topic,
            isPublic: response.debate.isPublic,
            isPublicType: typeof response.debate.isPublic,
            status: response.debate.status,
            keys: Object.keys(response.debate)
          });
          
          setDebate(response.debate);
          setTeam(response.team);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        logger.error('Error fetching team invite:', err);
        setError(t('invalidOrExpiredText'));
      } finally {
        setLoading(false);
      }
    };
    
    if (inviteCode && isAuthenticated) {
      fetchTeamInvite();
    } else if (inviteCode && !isAuthenticated) {
      // If not authenticated, wait for login
      setLoading(false);
      setError(t('pleaseLoginToViewText'));
    } else {
      setError(t('invalidInvitationLinkText'));
      setLoading(false);
    }
  }, [inviteCode, isAuthenticated, t]);
  
  // Handle joining the team
  const handleJoinTeam = async () => {
    if (!isAuthenticated) {
      // Store the invite code in local storage so user can be redirected after login
      localStorage.setItem('pendingTeamInvite', inviteCode || '');
      toast.error(t('pleaseLoginOrRegisterText'));
      navigate('/login');
      return;
    }
    
    try {
      setJoining(true);
      const captchaToken = await executeRecaptcha('join_team_invite');
      const response = await joinTeamByInviteCode(inviteCode!, user?.id, debate?.invitePasswordEnabled ? invitePassword : undefined, captchaToken || undefined);
      
      toast.success(t('successfullyJoinedTeamText'));
      
      // Navigate to the debate room
      navigate(`/debate/${response.debateId}`);
    } catch (err: any) {
      logger.error('Error joining team:', err);
      if (axios.isAxiosError(err) && err.response?.status === 403) {
        toast.error(t('invitePasswordIncorrect', 'Incorrect password. Please try again.'));
      } else if (axios.isAxiosError(err) && err.response?.status === 429) {
        toast.error(t('tooManyAttempts', 'Too many attempts. Please try again later.'));
        setError(t('tooManyAttempts', 'Too many attempts. Please try again later.'));
      } else if (axios.isAxiosError(err) && err.response?.status === 400) {
        toast.error(t('teamFull', 'This team is already full.'));
        setError(t('teamFull', 'This team is already full.'));
      } else {
        toast.error(t('failedToJoinTeamText'));
        setError(t('failedToJoinTeamRetryText'));
      }
    } finally {
      setJoining(false);
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
        >
          {t('returnToHomeText')}
        </Button>
      </Container>
    );
  }
  
  const teamLeaderName = team?.userId === user?.id 
    ? t('youAreText')
    : debate?.createdBy?.username || t('aFellowKnightText');
  
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom textAlign="center">
          {t('aCallToArms')}
        </Typography>
        
        <Alert severity="info" sx={{ my: 3 }}>
          <Typography variant="body1">
            {teamLeaderName} {t('callingForAlliesText')} <strong>{t('debateOfHonorText')}</strong> {t('onTheTopicText')}
          </Typography>
          <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold' }}>
            {debate?.topic}
          </Typography>
          
          {/* Add public/private debate notice */}
          <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
              <strong>{t('debateType')}</strong>&nbsp;
              {debate?.isPublic ? (
                <span>
                  <strong>{t('publicText')}</strong> - {t('publicDebateDescText')}
                </span>
              ) : (
                <span>
                  <strong>{t('privateText')}</strong> - {t('privateDebateDescText')}
                </span>
              )}
            </Typography>
          </Box>
        </Alert>
        
        {debate?.description && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('descriptionLabel')}
            </Typography>
            <Typography variant="body1">
              {debate.description}
            </Typography>
          </Box>
        )}
        
        <Divider sx={{ my: 3 }} />

        {/* Invite password field if required */}
        {debate?.invitePasswordEnabled && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('invitePasswordPrompt')}
            </Typography>
            <TextField
              fullWidth
              type="password"
              label={t('invitePasswordLabel')}
              value={invitePassword}
              onChange={(e) => setInvitePassword(e.target.value)}
              variant="outlined"
              sx={{ mb: 2, bgcolor: 'white' }}
            />
          </Box>
        )}
        
        <Typography variant="h5" gutterBottom>
          {t('joinTheNobleCause')}
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{t('fellowKnightText')}:</strong> {t('youAreBeingCalledText')} {teamLeaderName === t('youAreText') ? t('yourTeamText') : teamLeaderName} {t('inDefenseOfHonorText')}
          </Typography>
        </Alert>
        
        <Card variant="outlined" sx={{ mb: 3, bgcolor: 'white' }}>
          <CardContent>
            {/* Show the team viewpoint */}
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {t('yourTeamsPosition')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              {team?.viewpoint || t('teamPositionNotDefinedText')}
            </Typography>
            
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {t('yourTeamName')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              {team?.name || t('teamNotNamedText')}
            </Typography>
            
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {t('currentMembers')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              {team?.members > 0 
                ? `${team.members} ${team.members > 1 ? t('knightsText') : t('knightText')} ${team.members === 1 ? t('standsText') : t('standText')} ${t('readyText')}`
                : t('noKnightsJoinedText')}
            </Typography>
          </CardContent>
        </Card>
        
        {!isAuthenticated && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t('mustBeLoggedInText')}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/')}
          >
            {t('declineTheCall')}
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            onClick={handleJoinTeam}
            disabled={joining}
          >
            {joining ? <CircularProgress size={24} /> : t('joinTheRanks')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default TeamInvitePage; 